import { useForm, FormProvider } from 'react-hook-form';
import {
  mapCompanySize,
  mapProjectSize,
  mapHourlyRate,
  mapCountry,
  EnumProjectSize,
  EnumProjectHourlyRate,
  EnumCompanySize,
  EnumCountry,
} from 'tdc-web-backend/enums/enums';
import { Stack, Divider, Typography } from '@mui/material';
import { ReactComponent as SuitcaseCheckIcon } from '../../../../../../../../../../assets/icons/layout-icons/SuitcaseCheckIcon.svg';
import ConfirmModal from '../../../../../../../../../../components/confirm-modal/ConfirmModal';
import CustomInput from '../../../../../../../../../../components/custom-input/CustomInput';
import { primaryLight } from '../../../../../../../../../../utils/color';
import { useRefresh } from '../../../../../../../../../../utils/hooks/crud-hooks/useRefresh';
import useUserCompany from '../../../../../../../../../../utils/hooks/useUserCompany';
import { useTranslation } from 'react-i18next';

interface CompanyOverviewEditModalProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const CompanyOverviewEditModal = ({
  isModalOpen,
  setIsModalOpen,
}: CompanyOverviewEditModalProps) => {
  const refresh = useRefresh();
  const { company: companyData, updateCompany, isLoadingUpdateCompany } = useUserCompany();
  const { t } = useTranslation();
  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      averageProjectSize: companyData?.averageProjectSize as EnumProjectSize | '',
      hourlyRate: companyData?.hourlyRate as EnumProjectHourlyRate | '',
      companySize: companyData?.companySize as EnumCompanySize | '',
      headquartersLocation: {
        country: companyData?.headquartersLocation?.country as EnumCountry | '',
        city: companyData?.headquartersLocation?.city as string | '',
      },
    },
  });

  const onSubmit = (data: any) => {
    updateCompany(data, {
      onSuccess: () => {
        refresh();
        setIsModalOpen(false);
      },
    });
  };

  return (
    <ConfirmModal
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      confirmButtonText={t('common.save')}
      onCancel={() => setIsModalOpen(false)}
      onConfirm={formMethods.handleSubmit(onSubmit)}
      title={t('settings.companyProfile.overview')}
      width="40%"
      automaticallyCloseModalOnButtonClick={false}
      isLoading={isLoadingUpdateCompany}
      iconProps={{
        icon: <SuitcaseCheckIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: '#E3DBFC',
      }}
      message={
        <FormProvider {...formMethods}>
          <Stack
            sx={{
              maxHeight: '398px',
              overflowY: 'auto',
              fontSize: '12px',
              fontFamily: 'Gellix-SemiBold',
            }}
            spacing={3}
            pr="10px"
          >
            <Stack spacing={3}>
              <Typography variant="heading4" color="primaryDark.500">
                {t('settings.companyProfile.overviewDetails.headquartersLocation')}
              </Typography>

              <Divider />

              <CustomInput
                rules={{ required: t('errors.fieldIsRequired') }}
                name="headquartersLocation.country"
                label={t('common.country')}
                input={<CustomInput.Autocomplete />}
                choices={mapCountry}
                translation={{ t, ns: 'enum' }}
                placeholder={t('common.selectCountry')}
                sx={{ minHeight: 'none' }}
                disableClearable
              />

              <CustomInput
                rules={{ required: t('errors.fieldIsRequired') }}
                input={<CustomInput.Text />}
                name="headquartersLocation.city"
                label={t('common.city')}
                placeholder={`${t('common.type')} ${t('common.city')}`}
                sx={{ minHeight: 'none' }}
                disableClearable
              />
            </Stack>

            <Divider />

            <Stack spacing={3}>
              <CustomInput
                rules={{ required: t('errors.fieldIsRequired') }}
                input={<CustomInput.Autocomplete />}
                name="companySize"
                label={t('settings.companyProfile.overviewDetails.companySize')}
                placeholder={t('common.select')}
                choices={mapCompanySize}
                sx={{ minHeight: 'none' }}
                disableClearable
              />

              <CustomInput
                input={<CustomInput.Autocomplete />}
                rules={{ required: t('errors.fieldIsRequired') }}
                name="averageProjectSize"
                label={t('settings.companyProfile.overviewDetails.avgProjectSize')}
                placeholder={t('common.select')}
                choices={mapProjectSize}
                sx={{ minHeight: 'none' }}
                disableClearable
              />

              <CustomInput
                input={<CustomInput.Autocomplete />}
                rules={{ required: t('errors.fieldIsRequired') }}
                name="hourlyRate"
                label={t('settings.companyProfile.overviewDetails.hourlyRate')}
                placeholder={t('common.select')}
                choices={mapHourlyRate}
                sx={{ minHeight: 'none' }}
                disableClearable
              />
            </Stack>
          </Stack>
        </FormProvider>
      }
    />
  );
};

export default CompanyOverviewEditModal;
